import { useState, useEffect, type ChangeEvent, type ReactNode } from "react";
import { MdDelete } from "react-icons/md";
import Modal from "@modalSrc/Modal"; 
import ModalFormFields from "@modalSrc/ModalFormFields";
import RightTimeApi from "@/API";

type GLAccount = {
  id?: number;
  accountName?: string;
  accountNumber?: string;
  description?: string;
  type?: number;
  isActive?: boolean;
};

type GLAccountForm = {
  accountName: string;
  accountNumber: string;
  description: string;
  type: number;
  isActive: boolean;
};

type GLAccountModalProps = {
  isOpen: boolean;
  account: GLAccount | null;
  onClose: () => void;
  updateSpinner: (isLoading: boolean) => void;
};


const ACCOUNT_TYPE_OPTIONS = [
  { value: 0, label: "Asset" },
  { value: 1, label: "Liability" },
  { value: 2, label: "Equity" },
  { value: 3, label: "Revenue" },
  { value: 4, label: "Expense" },
];

export default function GLAccountModal({ isOpen, account, onClose, updateSpinner }: GLAccountModalProps) {
  const [form, setForm] = useState<GLAccountForm>({
    accountName: "",
    accountNumber: "",
    description: "",
    type: 0,
    isActive: true,
  });

  useEffect(() => {
    if (account) {
      setForm({
        accountName: account.accountName || "",
        accountNumber: account.accountNumber || "",
        description: account.description || "",
        type: account.type ?? 0,
        isActive: account.isActive !== false,
      });
    } else {
      setForm({
        accountName: "",
        accountNumber: "",
        description: "",
        type: 0,
        isActive: true,
      });
    }
  }, [account, isOpen]);

  if (!isOpen) return null;

  async function onSave(accountData: GLAccount) {
    updateSpinner(true);
    try {
      let resp;
      if (accountData.id && accountData.id > 0) 
        resp = await RightTimeApi.sendAsync("/GL_Account", form);
      if (resp && resp.ok) {
        onClose();
      } 
    } catch (err) {
      console.error("Error saving GL account:", err);
    } finally {
      updateSpinner(false);
    }
  }

  async function onDelete(accountId) {
    if (!window.confirm("Delete this GL account?")) return;
    updateSpinner(true);
    try {
      const resp = await RightTimeApi.deleteAsync(`/GL_Account/${accountId}`);
      if (resp && resp.ok) {
        onClose();
      } else {
        console.error("Failed to delete GL account:", resp.status, resp.statusText);
      }
    } catch (err) {
      console.error("Error deleting GL account:", err);
    } finally {
      updateSpinner(false);
    }
  }

  function handleChange(e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) {
    const { name, value, type } = e.target;
    const checked = type === "checkbox" && "checked" in e.target ? e.target.checked : false;
    setForm(f => ({
      ...f,
      [name]: type === "checkbox" ? checked : value,
    }));
  }

  function handleSubmit() {
    if (!form.accountName.trim() || !form.accountNumber.trim()) return;
    onSave({ ...account, ...form });
  }

  // Footer styled like UserEditModal, but without Cancel button
  const footer = (
    <>
      <div className="footerLeft">
        {account && account.id > 0 && (
          <button
            type="button"
            className="btn btn-danger iconButton"
            onClick={() => onDelete(account.id)}
            title="Delete GL Account"
          >
            <MdDelete />
          </button>
        )}
      </div>
      <div className="footerRight">
        <button
          type="button"
          className={`btn btn-primary${!form.accountName.trim() || !form.accountNumber.trim() ? ' disabled' : ''}`}
          onClick={handleSubmit}
          disabled={!form.accountName.trim() || !form.accountNumber.trim()}
        >
          Save
        </button>
      </div>
    </>
  );

  // Build form fields object for ModalFormFields
  const fields: Record<string, ReactNode> = {
    "Account Name": <input name="accountName" value={form.accountName} onChange={handleChange} required />,
    "Account Number": <input name="accountNumber" value={form.accountNumber} onChange={handleChange} required />,
    "Description": <input name="description" value={form.description} onChange={handleChange} />,
    "Type": (
      <select name="type" value={form.type} onChange={handleChange}>
        {ACCOUNT_TYPE_OPTIONS.map(opt => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    ),
    "Active": <input type="checkbox" name="isActive" checked={form.isActive} onChange={handleChange} />,
  };

  return (
    <Modal
      isOpen={isOpen}
      title={account && account.id > 0 ? "Edit GL Account" : "Add GL Account"}
      onClose={onClose}
      footer={footer}
      className="glAccountModal"
    >
      <ModalFormFields fields={fields} />
    </Modal>
  );
}


